/**
 * 鉴权上下文（H5 现场）—— 真实 JWT 登录态（替换原 mock 角色校验）。
 * 包裹于 App 最外层。
 *
 * 后端登录白名单：/api/system/user/login，响应 data：{ token, userId, username, orgId, platform }。
 * 注意：JWT 不含角色信息，现场角色由服务端按 userId 驱动；前端不再用 role 过滤待办
 * （/fulfillment/todo 已按当前登录用户返回其任务）。current 仅保留为兼容占位字段。
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import { login as apiLogin, logout as apiLogout } from '@/api/auth';
import { getToken } from '@/api/request';
import { getJSON, setJSON } from '@/utils/storage';
import { loadNavCreds, saveNavCredsUsernameOnly, clearNavCreds } from '@/utils/navCreds';
import { MobileRole } from '@/types/role';

/** 记住我 —— localStorage key */
const REMEMBER_KEY = 'bp_field_remember';

interface UserInfo {
  userId: string;
  username: string;
  orgId: string;
  platform: boolean;
}

interface LoginInfo {
  /** 登录账号（username，用于回显或兜底） */
  name: string;
  /** 真实姓名/昵称（优先中文展示） */
  displayName: string;
  /** 头像首字母 */
  initial: string;
  location: string;
  title: string;
}

interface RoleContextValue {
  /** 是否已登录 */
  isLoggedIn: boolean;
  /** 兼容占位（真实角色由后端驱动，前端不用于任务过滤） */
  current: MobileRole;
  /** 当前登录用户信息（展示用） */
  loginInfo: LoginInfo | null;
  /** 当前登录用户凭证信息 */
  userInfo: UserInfo | null;
  /** 登录：传入账号密码，成功返回 true */
  login: (username: string, password: string) => Promise<boolean>;
  /** 登出：清登录态 + token */
  logout: () => void;
}

const STORAGE_KEY = 'bp_field_user';

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

function readInitial(): { user: UserInfo; info: LoginInfo } | null {
  const stored = getJSON<{ user: UserInfo; info: LoginInfo } | null>(STORAGE_KEY, null);
  if (stored && stored.user && stored.info && getToken()) {
    // 兼容旧缓存：早期缓存没有 displayName，用 name 兜底并补首字母
    if (!stored.info.displayName) {
      stored.info.displayName = stored.info.name;
      stored.info.initial = (stored.info.displayName || '?').slice(0, 1).toUpperCase();
    }
    return stored;
  }
  return null;
}

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const initial = readInitial();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(Boolean(initial));
  const [userInfo, setUserInfo] = useState<UserInfo | null>(initial?.user ?? null);
  const [loginInfo, setLoginInfo] = useState<LoginInfo | null>(initial?.info ?? null);
  // current 保留为兼容占位（真实角色由后端按 userId 驱动，前端不再用于任务过滤）
  const [current] = useState<MobileRole>(MobileRole.ORIGIN);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    try {
      const res = await apiLogin(username, password);
      if (!res?.token) return false;
      const user: UserInfo = {
        userId: res.userId,
        username: res.username,
        orgId: res.orgId,
        platform: res.platform,
      };
      const displayName = res.realName?.trim() || res.username;
      const info: LoginInfo = {
        name: res.username,
        displayName,
        initial: (displayName || '?').slice(0, 1).toUpperCase(),
        location: res.orgId,
        title: res.platform ? '平台管理' : '现场操作员',
      };
      setUserInfo(user);
      setLoginInfo(info);
      setIsLoggedIn(true);
      setJSON(STORAGE_KEY, { user, info });
      return true;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    try {
      apiLogout();
    } catch {
      /* ignore */
    }
    setUserInfo(null);
    setLoginInfo(null);
    setIsLoggedIn(false);
    localStorage.removeItem(STORAGE_KEY);
    // 「记住我」账密降级：保留用户名回显、清除密码（共用设备/换账号不残留）
    const saved = loadNavCreds();
    if (saved?.u) saveNavCredsUsernameOnly(saved.u);
    else clearNavCreds();
    // 清理旧版记住我 key（仅用户名），统一由 bp_nav_creds_mobile 承载
    localStorage.removeItem(REMEMBER_KEY);
  }, []);

  return (
    <RoleContext.Provider value={{ isLoggedIn, current, loginInfo, userInfo, login, logout }}>
      {children}
    </RoleContext.Provider>
  );
}

/** 获取当前登录态与登录方法（须在 RoleProvider 内部使用） */
export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole 必须在 RoleProvider 内部使用');
  return ctx;
}
